import { PageHeader } from "../../components/organisms/PageHeader";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import './EmailTemplateNewDetails.styles.css'
import { useFormik } from "formik";
import { getListEmailTemplate } from "../../api/emailTriage";
import * as yup from "yup";
import { Box, Checkbox, Drawer, FormControlLabel, FormHelperText, Grid, Stack, Typography } from "@mui/material";
import { ITemplate } from "./interfaces";
import { LoadingPage } from "../../components/organisms/LoadingPage";
import Skeleton from "@mui/material/Skeleton";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import { Editor } from "../../components/molecules/Editor";
import { TagInput, TagInputTypes } from "../../components/atoms/TagInput";
import TokenTag from "../../components/atoms/TokenTag/TokenTag";
import { Toast } from "../../components/atoms/Toast";
import LeavePageDialog from "../../components/organisms/LeavePageDialog/LeavePageDialog";
import { useCallbackPrompt } from "../../hooks/useCallbackPrompt";
import Button from "@mui/material/Button";
import { Jodit } from "jodit/types/jodit";
import validator from "validator";
import { TEMPLATE_TOKENS } from "../../constants/contants";
import { useTranslation } from "react-i18next";
import { insertTextAtCursor, isEditorContentEmpty, validateRequiredMax } from "../../utils/common";

const DEFAULT_REPORTER_EMAIL = TEMPLATE_TOKENS.REPORTER_EMAIL;

const newDetailsTemplateLinks = (isNew = false) => [
  {
    name: "Email Templates",
    href: "/settings/email-templates",
  },
  {
    name: isNew ? 'New Template' : 'Template Details',
  },
];

const defaultTemplate: ITemplate = {
  id: '',
  name: '',
  desc: '',
  cat: '',
  cc: '',
  content: '',
  created_by: '',
  subject: '',
  to: '',
  status: 'active',
  type: 'custom',
  created_at: 0,
  deleted_at: 0,
  updated_at: 0,
  zone_id: -1,
  include_reporter: false,
  read_only: false,
}

const tokens = [
  {
    content: TEMPLATE_TOKENS.SENDER,
    tooltip: 'When the email is sent, it will appear as the email of the person who reported the email.',
  },
  {
    content: TEMPLATE_TOKENS.STATUS,
    tooltip: <div id="status-token-tooltip">When the email is sent, it will appear as the final status of the email:
      <ul>
        <li>"No Threat"</li>
        <li>"Threat Identified" (Threat/Undetermined).</li>
      </ul>
    </div>,
  },
  {
    content: TEMPLATE_TOKENS.DOWNLOAD_REPORTED_EMAIL,
    tooltip: 'When the email is sent, it will appear as a link that users can click on it to download the reported email.',
  },
  {
    content: TEMPLATE_TOKENS.REPORTED_EMAIL_SUBJECT,
    tooltip: 'When the email is sent, it will appear  as the Email subject of the reported email.',
  },
];
const processEmails = (emailsString: string) => emailsString ? emailsString.split(',').filter(email => email) : [];

const EmailTemplateNewDetails: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { id: template_id } = useParams();
  const [template, setTemplate] = useState<ITemplate>(defaultTemplate);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [toastValue, setToastValue] = useState({ type: '', message: '', isOpen: false, toastId: 0 });
  const [modalID, setModalID] = useState(1);
  const [validateEmailContent, setValidateEmailContent] = useState(false);
  const [editorText, setEditorText] = useState<string>(template.content);
  const [loadingEditor, setLoadingEditor] = useState(false)
  const [templateEditorError, setTemplateEditorError] = useState(false);
  const [isDisable, setIsDisable] = useState<boolean>(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState<boolean>(false);
  const [tags, setTags] = useState({ to: [] as string[], cc: [] as string[] });
  const [tagInputValues, setTagInputValues] = useState({ to: '', cc: '' });
  const [tagInputs, setTagInputs] = useState({ to: '', cc: '' });
  const [initialTags, setInitialTags] = useState({ to: [] as string[], cc: [] as string[] });
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showLeaveDialog);
  const [paramSendMail, setParamSendMail] = useState({
    subject: template.subject,
    to: processEmails(template.to),
    cc: processEmails(template.cc),
    reportedBy: template.created_by || TEMPLATE_TOKENS.REPORTER_EMAIL,
    is_include_reporter: template.include_reporter,
  });
  const [flagHelperTextName, setFlagHelperTextName] = useState(true);
  const [flagHelperTextDesc, setFlagHelperTextDesc] = useState(true);
  const [joditInstance, setJoditInstance] = useState<Jodit | undefined>();
  const [contentHasBeenFocused, setContentHasBeenFocused] = useState(false);
  const [createdTemplateId, setCreatedTemplateId] = useState();

  const tagRefTo: any = useRef();
  const tagRefCC: any = useRef();
  const subjectRef: any = useRef();

  const formTouchedHandler = () => {
    setOpenDrawer(true);
    setShowLeaveDialog(true);
  }

  const isDefaultTemplateType = template.type === 'default';
  const isActiveElement = (element: Element) => element === document.activeElement;

  const validateField = (value: string, type?: string) => {
    /* istanbul ignore next */
    if (!value.trim() || !value.trim().replace(/<\/?[^>]+(>|$)/g, "")) {
      setTemplateEditorError(true);
    } else {
      setTemplateEditorError(false);
    }
  }

  const isInvalidEmailTag = (value = '') => value.split(',').some(val => val !== '' && !(validator.isEmail(val) || val === TEMPLATE_TOKENS.SENDER || val === TEMPLATE_TOKENS.REPORTER_EMAIL));

  const validationSchema = yup.object({
      name: yup
        .string()
        .trim()
        .required(t('validation.required', { field: 'Template name' })),
      desc: yup
        .string()
        .trim()
        .required(t('validation.required', { field: 'Template Description' })),
      content: yup
        .string()
        .trim()
        .required(t('validation.required', { field: 'Template Content' }))
        .test('Empty content', t('validation.required', { field: 'Template Content' }), (value) => !isEditorContentEmpty(value)),
      to: yup
        .string()
        .test('Incorrect To tags', 'Invalid email address tags', (value) => !isInvalidEmailTag(value)),
      cc: yup
        .string()
        .test('Incorrect CC tags', 'Invalid email address tags', (value) => !isInvalidEmailTag(value)),
    }
  );

  const formik = useFormik({
    initialValues: {
      ...defaultTemplate,
      status: defaultTemplate.status === 'active',
    },
    validationSchema: validationSchema,
    onSubmit: (values: any) => {
      setFlagHelperTextDesc(true);
      setFlagHelperTextName(true);
      setContentHasBeenFocused(false);
      setLoading(true);
      const payload = {
        ...values,
        content: editorText,
        status: values.status ? 'active' : 'inactive',
      }

      if (template_id) {
        getListEmailTemplate({ action: 'update', payload }).then(res => {
          if (res && res.status === 200) {
            setModalID(modalID + 1);
            setToastValue({ type: 'success', message: t('messages.genericSuccess'), isOpen: true, toastId: modalID });
            setOpenDrawer(false);
            setShowLeaveDialog(false);
            setTemplate(payload);
          }
          setLoading(false);
        }).catch(err => {
          setLoading(false);
          console.error(err);
          setModalID(modalID + 1);
          setToastValue({ type: 'error', message: t('messages.genericError'), isOpen: true, toastId: modalID });
        });
      } else {
        getListEmailTemplate({ action: 'create', payload }).then(res => {
          if (res && res.status === 200) {
            const newTemplateId = res.data?.id
            setModalID(modalID + 1);
            setToastValue({ type: 'success', message: t('messages.genericSuccess'), isOpen: true, toastId: modalID });
            setOpenDrawer(false);
            setShowLeaveDialog(false);
            setCreatedTemplateId(newTemplateId);
          }
          setLoading(false);
        }).catch(err => {
          setLoading(false);
          console.error(err);
          setModalID(modalID + 1);
          setToastValue({ type: 'error', message: t('messages.genericError'), isOpen: true, toastId: modalID });
        })
      }
    },
  });

  const helperTextName = React.useMemo(() => {
      if (formik.values.name && formik.values.name.length > 254) {
          return t('validation.maxLength', { field: 'Template Name', max: 255 });
      }
      return '';
  }, [formik.values.name, flagHelperTextName]);

  const helperTextDescription = React.useMemo(() => {
      if (formik.values.desc && formik.values.desc.length > 254) {
          return t('validation.maxLength', { field: 'Template Description', max: 255 });
      }
      return '';
  }, [formik.values.desc]);

  useEffect(() => {
    if (template_id) {
      setLoading(true);
      getListEmailTemplate({ action: 'get', payload: { template_id } }).then(res => {
        if (res && res.status === 200) {
          setTemplate(res.data);
          setLoading(false);
        }
      }).catch(err => {
        console.error("err: ", err);
        setLoading(false);
      });
    }
  }, [template_id]);

  useEffect(() => {
    formik.setValues({ ...template, status: template.status === 'active' });
    const { subject, to, cc, created_by, include_reporter, content } = { ...template };

    setEditorText(content);
    setTags({ to: processEmails(to), cc: processEmails(cc) });
    setInitialTags({ to: processEmails(to), cc: processEmails(cc) });

    setParamSendMail({
      subject,
      to: processEmails(to),
      cc: processEmails(cc),
      reportedBy: created_by || DEFAULT_REPORTER_EMAIL,
      is_include_reporter: include_reporter,
    });
  }, [template]);

  useEffect(() => {
    formik.setValues({ ...formik.values, content: editorText });
  }, [editorText]);

  useEffect(() => {
    formik.setValues({
      ...formik.values,
      to: [...tags.to, tagInputs.to].join(','),
      cc: [...tags.cc, tagInputs.cc].join(',')
    });
  }, [tags, tagInputs]);

  useEffect(() => {
    if (formik.isSubmitting) {
      setContentHasBeenFocused(true);
    }
  }, [formik.isSubmitting]);

  useEffect(() => {
    if (contentHasBeenFocused) {
      formTouchedHandler();
    }
  }, [contentHasBeenFocused])

  useEffect(() => {
    if (!showLeaveDialog && createdTemplateId) {
      navigate(`/settings/email-templates/${createdTemplateId}`);
    }
  }, [navigate, createdTemplateId, showLeaveDialog]);

  const onValidation = () => {
    if (!formik.isValid) {
      setModalID(modalID + 1);
      setToastValue({ type: 'error', message: 'Please complete the required fields.', isOpen: true, toastId: modalID });
    }
  }

  const tokenClickHandler = (token: string) => {
    const toFocused = isActiveElement(tagRefTo?.current);
    const ccFocused = isActiveElement(tagRefCC?.current);
    const subjectFocused = isActiveElement(subjectRef?.current);

    if (!isDefaultTemplateType) {
      if (joditInstance?.editorIsActive) {
        joditInstance?.selection?.insertHTML(token);
        // @ts-ignore
        const newEditorText = joditInstance?.currentPlace?.element?.value
        setEditorText(newEditorText);
      } else if (subjectFocused) {
        insertTextAtCursor(subjectRef.current, token);
        formik.setValues({ ...formik.values, subject: subjectRef?.current?.value });
      } else {
        if (token === TEMPLATE_TOKENS.SENDER) {
          if (toFocused || ccFocused) {
            const tagsType = toFocused ? 'to' : 'cc';
            if (!tags[tagsType].includes(token)) {
              const newTags = [...tags[tagsType], token];
              setTags({ ...tags, [tagsType]: newTags });
            }
          }
        }
      }
    }
  }

  const TokensComponent: React.FC = () => {
    return (<Stack className="tokens-component" gap={5}>
      <Stack gap={2}>
        <Typography variant="subtitle2" fontWeight={600}>AVAILABLE TOKENS</Typography>
        <Typography variant="body2">You can use these tokens in the <strong>To, CC, Subject,</strong> and <strong>Email
          Content</strong> fields. Click a token or type it directly into the field where you want it to appear.</Typography>
      </Stack>
      <Stack gap={2}>
        {tokens?.map((token, index) =>
          <TokenTag key={`token-${index}`} token={token.content}
                    clickHandler={tokenClickHandler} tooltip={token.tooltip}/>)}
      </Stack>
    </Stack>)
  }

  const handleDiscard = () => {
    formik.setValues({ ...template, status: template.status === 'active' });
    setOpenDrawer(false);
    setShowLeaveDialog(false);
    setTags(initialTags);
    setEditorText(template.content);
    setContentHasBeenFocused(false);
    tagRefTo.current.value = "";
    tagRefCC.current.value = "";
    setTagInputs({ to: '', cc: '' });
    formik.setTouched({}, false);
  }

  const drawerButtons = () => (
    <Box
      sx={{ height: '72px' }}
      role="presentation"
      component="span"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Stack spacing={2} direction="row">
        <Button variant="outlined" style={{ color: '#345C8B', fontFamily: 'Metropolis' }}
                onClick={() => handleDiscard()} data-testid="discardBtn">{t('actions.discard')}</Button>
        <Button variant="contained" style={{ backgroundColor: '#345C8B', fontFamily: 'Metropolis' }}
                type={"submit"} data-testid="saveBtn">{t('actions.saveChanges')}</Button>
      </Stack>
    </Box>
  );

  const tagsChangeHandler = (type: string, newTags: string[]) => {
    setTags({ ...tags, [type]: newTags });
    formTouchedHandler();
  }

  const handleChangeMultiline = (e: any) => {
    formTouchedHandler();
    if (e?.target?.value.toString().length > 255) {
      formik.setFieldValue('desc', e.target.value.slice(256, e?.target?.value.toString().length - 1));
    }
  };

  const handleTagInputChange = (type: string, e: any) => {
    setTagInputs({ ...tagInputs, [type]: e.target.value });
  }

  return (
    <>
      <form
        onSubmit={(value) => {
          formik.handleSubmit(value);
          onValidation();
        }}
      >
        <div className='email-template-new-details-page page-content-wrapper'>
          <PageHeader
            pageTitle={template_id ? <Stack direction="row" gap={2} alignItems="center">
              <Box className={`template_type template_type_${template?.type}`}>
                <span>{template.type && `${template.type[0].toUpperCase()}${template.type.slice(1)}`}</span>
              </Box>
              {template.name}
            </Stack> : t('pageTitles.templates.create')}
            breadCrumbData={newDetailsTemplateLinks(!template_id)}
          />

          <Stack className='template-box'>
            {!loading ? <label className={'box-title'}>
              Template Information
            </label> : <Skeleton animation="wave" width={300} height={60}/>}

            {!loading ? <InputLabel className={'input-label'}>
              {t('fields.templates.name')}<span style={{ color: 'red', marginLeft: 6 }}>*</span>
            </InputLabel> : <Skeleton animation="wave" width={200} height={20}/>}
            {!loading ? <OutlinedInput
              name={'name'}
              id={'template_name'}
              data-testid={'inputTemplateName'}
              className={'input-field'}
              classes={{
                root: 'input-template-name'
              }}
              placeholder={'Enter email template name here'}
              size={"small"}
              value={formik.values.name}
              onChange={(e) => {
                formik.handleChange(e);
                formTouchedHandler();
              }}
              error={formik.touched.name && Boolean(formik.errors.name)}
              onBlur={formik.handleBlur}
              inputProps={{
                maxLength: 255,
              }}
              onFocus={() => {
                formTouchedHandler();
                setFlagHelperTextName(false)
              }}
              disabled={isDefaultTemplateType}
            /> : <Skeleton animation="wave" sx={{ width: '400px', height: '50px' }}/>}
            <FormHelperText error id="name"
                            style={{ margin: '0 0 24px 16px', fontWeight: 500, fontSize: '12px' }}>
              {Boolean(formik.errors.name) ? (formik.touched.name && formik.errors.name) : (!flagHelperTextName && helperTextName)}
            </FormHelperText>

            {!loading ? <InputLabel className={'input-label'}>
                {t('fields.templates.description')}<span style={{ color: 'red', marginLeft: 6 }}>*</span>
              </InputLabel> :
              <Skeleton animation="wave" sx={{ width: '200px', height: '20px' }}/>}
            {!loading ? <OutlinedInput
              name={'desc'}
              id={'template_description'}
              className={'input-field'}
              style={{ height: '96px' }}
              multiline={true}
              rows={4}
              placeholder='Enter description'
              value={formik.values.desc}
              onChange={(e) => {
                formik.handleChange(e);
                handleChangeMultiline(e)
              }}
              error={formik.touched.desc && Boolean(formik.errors.desc)}
              onBlur={formik.handleBlur}
              inputProps={{
                maxLength: 255,
              }}
              onFocus={() => {
                setFlagHelperTextDesc(false)
              }}
              disabled={isDefaultTemplateType}
            /> : <Skeleton animation="wave" sx={{ width: '400px', height: '100px' }}/>}
            <FormHelperText error id="description"
                            style={{ margin: '0 0 24px 16px', fontWeight: 500, fontSize: '12px' }}>
              {Boolean(formik.errors.desc) ? (formik.touched.desc && formik.errors.desc) : (!flagHelperTextDesc && helperTextDescription)}
            </FormHelperText>

            {!loading ? <InputLabel className={'input-label'}>
              {t('fields.templates.status')}
            </InputLabel> : <Skeleton animation="wave" sx={{ width: '200px', height: '20px' }}/>}
            {!loading ? <FormControlLabel
              control={
                <Checkbox
                  data-testid={'ckBoxStatus'}
                  name={'status'}
                  id={'status'}
                  checked={formik.values.status}
                  onChange={(e) => {
                    formTouchedHandler();
                    formik.handleChange(e);
                  }}
                  color="success"/>
              }
              style={{ width: "fit-content" }}
              label={"Active"}
            /> : <Skeleton animation="wave" sx={{ width: '200px', height: '30px' }}/>}
          </Stack>

          <Grid container xs={12} className={`template-box content-box ${openDrawer ? 'mg-bottom' : ''}`}>
            <Grid item xs={7} className={'template-box-left'}>
              {!loading ? <label className={'box-title'}>Template Content</label> :
                <Skeleton animation="wave" sx={{ width: '300px', height: '60px' }}/>}

              <Box marginTop={4}></Box>

              <Box className="tag-input-container">
                {!loading ? <InputLabel className={'input-label'}>{t('fields.templates.to')}</InputLabel> :
                  <Skeleton animation="wave" width={200} height={20}/>}
                {!loading ? <TagInput
                  tags={tags.to}
                  handleModifyTags={tagsChangeHandler}
                  tagRef={tagRefTo}
                  type={TagInputTypes.TO}
                  paramSendMail={paramSendMail}
                  setParamSendMail={setParamSendMail}
                  disabled={isDefaultTemplateType}
                  focusHandler={() => formTouchedHandler()}
                  changeHandler={handleTagInputChange}
                  inputValue={tagInputValues.to}
                  setInputValue={(value) => setTagInputValues((prev) => ({...prev, to: value}))}
                /> : <Skeleton animation="wave" sx={{ width: '400px', height: '50px' }}/>}
              </Box>

              <Box className="tag-input-container">
                {!loading ? <InputLabel className={'input-label'}>{t('fields.templates.cc')}</InputLabel> :
                  <Skeleton animation="wave" width={200} height={20}/>}
                {!loading ? <TagInput
                    tags={tags.cc}
                    handleModifyTags={tagsChangeHandler}
                    tagRef={tagRefCC}
                    type={TagInputTypes.CC}
                    paramSendMail={paramSendMail}
                    setParamSendMail={setParamSendMail}
                    disabled={isDefaultTemplateType}
                    focusHandler={() => formTouchedHandler()}
                    checkboxInput
                    changeHandler={handleTagInputChange}
                    inputValue={tagInputValues.cc}
                    setInputValue={(value) => setTagInputValues((prev) => ({...prev, cc: value}))}
                  /> :
                  <Skeleton animation="wave" sx={{ width: '400px', height: '50px' }}/>}
              </Box>

              {!loading ? <InputLabel className={'input-label'}>{t('fields.templates.subject')}</InputLabel> :
                <Skeleton animation="wave" width={200} height={20}/>}
              {!loading ? <OutlinedInput
                name={'subject'}
                id={'template_subject'}
                data-testid={'inputTemplateSubject'}
                className={'input-field'}
                classes={{
                  root: 'input-template-subject'
                }}
                onFocus={() => formTouchedHandler()}
                placeholder="Input subject here"
                size={"small"}
                value={formik.values.subject}
                onChange={(e) => {
                  formik.handleChange(e);
                }}
                error={formik.touched.subject && Boolean(formik.errors.subject)}
                onBlur={formik.handleBlur}
                disabled={isDefaultTemplateType}
                inputRef={subjectRef}
              /> : <Skeleton animation="wave" sx={{ width: '400px', height: '50px' }}/>}
              <FormHelperText error id="subject-helper-text"
                              style={{ margin: '0 0 24px 16px', fontWeight: 500, fontSize: '12px' }}>
                {(formik.errors.subject && formik.touched.subject) && formik.errors.subject}
              </FormHelperText>

              {!loading ? <InputLabel className={'input-label'}>
                {t('fields.templates.content')}<span style={{ color: 'red', marginLeft: 6 }}>*</span>
              </InputLabel> : <Skeleton animation="wave" width={200} height={20}/>}
              {!loading ? <Editor
                formikError={contentHasBeenFocused ? formik.errors.content as string : ''}
                validateEmailContent={validateEmailContent}
                setValidateEmailContent={setValidateEmailContent}
                editorText={editorText}
                setEditorText={setEditorText}
                loadingEditor={loadingEditor}
                templateEditorError={templateEditorError}
                validateField={validateField}
                isDisable={isDisable}
                editorDisabled={isDefaultTemplateType}
                setIsDisable={setIsDisable}
                isConfirm={isConfirm}
                setIsConfirm={setIsConfirm}
                joditInstance={joditInstance}
                setJoditInstance={setJoditInstance}
                setHasBeenFocused={setContentHasBeenFocused}
              /> : <Skeleton animation="wave" sx={{ width: '400px', height: '100px' }}/>}
            </Grid>
            <Grid item xs={5} paddingLeft={2} marginTop={9.5} zIndex={2}>
              <TokensComponent/>
            </Grid>
          </Grid>
          <Drawer
            PaperProps={{
              sx: {
                boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.15)',
                zIndex: 100
              }
            }}
            anchor={'bottom'}
            open={openDrawer}
            hideBackdrop={false}
            variant={'persistent'}
          >
            {drawerButtons()}
          </Drawer>
        </div>
      </form>
      <Toast
        isOpen={toastValue.isOpen}
        type={toastValue.type}
        message={toastValue.message}
        toastId={toastValue.toastId}
        autoHideDuration={2000}
      />
      <LoadingPage open={loading}/>
      <LeavePageDialog
        showDialog={showPrompt}
        confirmNavigation={confirmNavigation}
        cancelNavigation={cancelNavigation}
      />
    </>
  );
};

export default EmailTemplateNewDetails;
